import React, { useState } from "react";
import { Archive, Calendar, Camera, FileText, Images, MapPin, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { usePagedList } from "../hooks/usePagedList";
import api, { formatError } from "../lib/api";
import { downloadPdf, FileUpload } from "./FileUpload";
import { Lightbox } from "./Lightbox";
import { LoadMore } from "./LoadMore";
import { Input } from "./ui/input";
import { timeRange } from "../lib/dates";

const src = (u) => (u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function AttendeesToggle({ e }) {
  const [names, setNames] = useState(null);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    if (!open && names === null) {
      try { const { data } = await api.get(`/events/${e.id}/attendance`); setNames(data.attendees); }
      catch (err) { toast.error(formatError(err.response?.data?.detail)); return; }
    }
    setOpen((o) => !o);
  };
  return (
    <span className="inline-flex flex-col">
      <button type="button" onClick={toggle} className="inline-flex items-center gap-1 underline decoration-dotted hover:text-crimson" data-testid={`past-event-attendance-${e.id}`}><Users size={12} /> {e.attendance_count} attended</button>
      {open && names && <span className="mt-1 max-w-md text-[11px] text-palm" data-testid={`past-event-attendees-${e.id}`}>{names.length ? names.join(" · ") : "No attendance recorded."}</span>}
    </span>
  );
}

function PhotoAttach({ event, onAdded }) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const attach = async (file) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/photos`, { url: file.url, caption });
      toast.success("Photo added to the archive"); setCaption(""); onAdded(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="mt-3 inline-flex min-h-[36px] items-center gap-1 text-sm font-semibold text-crimson hover:underline" data-testid={`past-event-add-photo-${event.id}`}><Camera size={14} /> Add photos</button>;
  return (
    <div className="mt-3 space-y-2 rounded-xl border border-gold/30 bg-parchment-card p-3" data-testid={`past-event-photo-form-${event.id}`}>
      <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" maxLength={200} data-testid={`past-event-photo-caption-${event.id}`} />
      <FileUpload purpose="gallery" accept="image/*" label={busy ? "Saving…" : "Choose photo"} crop={false} onUploaded={attach} testId={`past-event-photo-upload-${event.id}`} />
      <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:underline" data-testid={`past-event-photo-done-${event.id}`}>Done</button>
    </div>
  );
}

function PhotoGrid({ e, canEdit, onOpen, onRemove }) {
  if (e.photos.length === 0) return null;
  return (
    <div className="mt-3 grid grid-cols-4 gap-1.5" data-testid={`past-event-photos-${e.id}`}>
      {e.photos.slice(0, 4).map((p, i) => (
        <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg">
          <button type="button" onClick={() => onOpen(i)} className="h-full w-full cursor-zoom-in" data-testid={`past-event-photo-${p.id}`}>
            <img src={src(p.url)} alt={p.caption || e.title} className="h-full w-full object-cover" loading="lazy" />
            {i === 3 && e.photos.length > 4 && <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-sm font-bold text-parchment">+{e.photos.length - 4}</span>}
          </button>
          {canEdit && <button type="button" onClick={() => onRemove(p)} className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-parchment opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100" aria-label="Remove photo" data-testid={`past-event-photo-remove-${p.id}`}><Trash2 size={13} /></button>}
        </div>
      ))}
    </div>
  );
}

function MinutesLinks({ e }) {
  if (e.minutes.length === 0) return null;
  return (
    <ul className="mt-3 space-y-1" data-testid={`past-event-minutes-${e.id}`}>
      {e.minutes.slice(0, 5).map((m) => (
        <li key={m.id} className="flex items-center gap-2 text-sm">
          <FileText size={14} className="shrink-0 text-gold" />
          {m.file_url
            ? <button onClick={() => downloadPdf(m.file_url.replace(/^\/api/, ""), `${m.title}.pdf`)} className="min-h-[32px] text-left font-semibold text-palm hover:underline" data-testid={`past-event-minutes-link-${m.id}`}>{m.title}</button>
            : <a href={`#minutes-${m.id}`} className="min-h-[32px] font-semibold text-palm hover:underline" data-testid={`past-event-minutes-link-${m.id}`}>{m.title}</a>}
          {m.adopted && <span className="rounded-full bg-palm/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-palm">adopted</span>}
        </li>
      ))}
      {e.minutes.length > 5 && <li className="text-xs text-muted-foreground" data-testid={`past-event-minutes-more-${e.id}`}>+{e.minutes.length - 5} more — use the minutes search below and filter by this meeting</li>}
    </ul>
  );
}

function PastEventCard({ e, onPhoto, canEdit, onChange }) {
  const remove = async (p) => {
    if (!window.confirm("Remove this photo from the archive and gallery?")) return;
    try { await api.delete(`/admin/events/${e.id}/photos/${p.id}`); onChange({ ...e, photos: e.photos.filter((x) => x.id !== p.id) }); toast.success("Photo removed"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <div className="glass rounded-2xl p-5" data-testid={`past-event-${e.id}`}>
      <span className="eyebrow text-gold">{e.type}</span>
      <h4 className="mt-1 font-serif-display text-xl font-bold text-crimson">{e.title}</h4>
      <p className="mt-1 flex items-center gap-2 text-sm text-palm"><Calendar size={14} /> {e.date}{timeRange(e)}</p>
      {e.location && <p className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={14} /> {e.location}</p>}
      <p className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
        <AttendeesToggle e={e} />
        <span className="inline-flex items-center gap-1"><Images size={12} /> {e.photos.length} photos</span>
        <span className="inline-flex items-center gap-1"><FileText size={12} /> {e.minutes.length} minutes</span>
      </p>
      <PhotoGrid e={e} canEdit={canEdit} onOpen={(i) => onPhoto(e.photos, i)} onRemove={remove} />
      <MinutesLinks e={e} />
      {e.photos.length === 0 && e.minutes.length === 0 && <p className="mt-3 text-xs italic text-muted-foreground">No photos or minutes yet for this meeting.</p>}
      {canEdit && <PhotoAttach event={e} onAdded={(p) => onChange({ ...e, photos: [...e.photos, p] })} />}
    </div>
  );
}

export function PastEvents() {
  const { data, items, total, busy, loadMore, replace, hasMore } = usePagedList("/events/past?limit=6");
  const [lb, setLb] = useState(null);
  if (!data) return null;
  const canEdit = Boolean(data.can_add_photos);
  return (
    <section className="mt-10" data-testid="past-events">
      <h3 className="mb-3 flex items-center gap-2 font-serif-display text-2xl font-bold text-crimson"><Archive size={20} className="text-gold" /> Past events</h3>
      {total === 0 && <p className="text-sm text-muted-foreground" data-testid="past-events-empty">No past events yet.</p>}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((e) => <PastEventCard key={e.id} e={e} canEdit={canEdit} onChange={replace} onPhoto={(photos, index) => setLb({ items: photos, index })} />)}
      </div>
      {hasMore && <LoadMore remaining={total - items.length} busy={busy} onClick={loadMore} testId="past-events-load-more" />}
      {lb && <Lightbox items={lb.items} index={lb.index} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} src={src} />}
    </section>
  );
}
